import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PromptTuningCard from "@/components/PromptTuningCard";
import {
    Sparkles, Search, Plus, CheckCircle2, Circle, Wand2, Filter, Building2,
    Layers, ShieldCheck, Trash2, Star, Loader2, RefreshCw, Globe,
    Pencil, TrendingUp, TrendingDown, Activity, ClipboardList, Target,
    ExternalLink, ArrowUpRight, ArrowRight, AlertTriangle, X,
} from "lucide-react";

/**
 * Skill Center — the master skill library page.
 *
 * Layout (desktop):
 *   ┌─────────────────┬─────────────────────────────────────────────┐
 *   │  Industries     │  Search + Category tabs                     │
 *   │  (which ones    │  Skill grid (each with "we use this" chip)  │
 *   │  we operate in) │                                             │
 *   └─────────────────┴─────────────────────────────────────────────┘
 *
 * Roles:
 *   - Employer / Admin: full access, can toggle industries, mark skills
 *   - Recruiter: read + can toggle company skill selections (helpful during JD authoring)
 *   - Admin only: "Add new skill", "AI Generate more", "Approve pending", "Add industry"
 */
export default function SkillCenter() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const isEmployerOrAdmin = ["admin", "employer"].includes(user?.role);
    const hasCompany = !!user?.company_id;

    const [categories, setCategories] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [companyIndustries, setCompanyIndustries] = useState([]);
    const [companySkillIds, setCompanySkillIds] = useState(new Set());
    const [stats, setStats] = useState(null);

    // Grid state
    const [category, setCategory] = useState("");
    const [industry, setIndustry] = useState("");
    const [q, setQ] = useState("");
    const [onlyUsed, setOnlyUsed] = useState(false);
    const [onlyPending, setOnlyPending] = useState(false);
    const [skills, setSkills] = useState([]);
    const [skillsLoading, setSkillsLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 100;

    // Dialogs
    const [addOpen, setAddOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [addIndOpen, setAddIndOpen] = useState(false);
    const [discoverOpen, setDiscoverOpen] = useState(false);
    const [bulkAddOpen, setBulkAddOpen] = useState(false);
    const [migrateOpen, setMigrateOpen] = useState(false);
    const [editSkill, setEditSkill] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [reclassifyOpen, setReclassifyOpen] = useState(false);

    // View tab: library | trending | declining | gap
    const [view, setView] = useState("library");

    // ── data loads ─────────────────────────────────────────────────
    const loadStatic = useCallback(async () => {
        try {
            const [cats, inds, sel, ci, s] = await Promise.all([
                api.get("/skill-center/categories"),
                api.get("/skill-center/industries"),
                api.get("/company/skill-selections"),
                api.get("/company/industries"),
                api.get("/skill-center/stats"),
            ]);
            setCategories(cats.data.items || []);
            setIndustries(inds.data.items || []);
            setCompanySkillIds(new Set((sel.data.items || []).map((s) => s.id)));
            setCompanyIndustries(ci.data.industries || []);
            setStats(s.data);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => { loadStatic(); }, [loadStatic]);

    const loadSkills = useCallback(async () => {
        setSkillsLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (category) params.category = category;
            if (industry) params.industry = industry;
            if (q.trim().length >= 2) params.q = q.trim();
            if (onlyUsed) params.only_used = 1;
            if (onlyPending) params.only_pending = 1;
            const { data } = await api.get("/skill-center/skills", { params });
            setSkills(data.items || []);
            setTotal(data.total || 0);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSkillsLoading(false); }
    }, [category, industry, q, onlyUsed, onlyPending, page]);
    useEffect(() => { loadSkills(); }, [loadSkills]);

    // Reset page on filter change
    useEffect(() => { setPage(1); }, [category, industry, q, onlyUsed, onlyPending]);

    // ── industry toggle ───────────────────────────────────────────
    const toggleIndustry = async (slug) => {
        if (!isEmployerOrAdmin) return;
        if (!hasCompany) {
            toast.info("Industry selections are per-company — your admin account has no company profile.");
            return;
        }
        const next = companyIndustries.includes(slug)
            ? companyIndustries.filter((i) => i !== slug)
            : [...companyIndustries, slug];
        setCompanyIndustries(next);
        try {
            await api.put("/company/industries", { industries: next });
            toast.success("Industries saved");
        } catch (err) {
            toast.error(formatApiError(err));
            setCompanyIndustries(companyIndustries);   // revert
        }
    };

    // ── skill toggle ──────────────────────────────────────────────
    const toggleSkill = async (skill) => {
        if (!hasCompany) {
            toast.info("Skill selections are per-company — your admin account has no company profile.");
            return;
        }
        const already = companySkillIds.has(skill.id);
        const next = new Set(companySkillIds);
        if (already) next.delete(skill.id); else next.add(skill.id);
        setCompanySkillIds(next);
        // Optimistic stat bump so the strip reflects the change immediately
        setStats((cur) => cur ? { ...cur, company_selected_count: next.size } : cur);
        try {
            await api.put("/company/skill-selections",
                already ? { remove: [skill.id] } : { add: [skill.id] });
        } catch (err) {
            toast.error(formatApiError(err));
            setCompanySkillIds(companySkillIds);   // revert
            setStats((cur) => cur ? { ...cur, company_selected_count: companySkillIds.size } : cur);
        }
    };

    // ── admin: approve / delete ───────────────────────────────────
    const approveSkill = async (skill) => {
        try {
            await api.post(`/skill-center/skills/${skill.id}/approve`);
            toast.success(`${skill.name} approved`);
            loadSkills(); loadStatic();
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const deleteSkill = async (skill) => {
        if (!confirm(`Delete "${skill.name}" from the global catalog?`)) return;
        try {
            await api.delete(`/skill-center/skills/${skill.id}`);
            toast.success("Deleted");
            loadSkills(); loadStatic();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const catCounts = stats?.counts_by_category || {};

    return (
        <div className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-5" data-testid="skill-center-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Master library</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Skill Center</h1>
                    <p className="text-foreground/80 text-sm mt-2 max-w-2xl">
                        The world&apos;s desirable skills, tools, frameworks, softwares, certifications and soft skills — grouped by industry.
                        Pick the ones your company uses so we can power sharper Job Seeds, JDs, and candidate evaluations.
                    </p>
                </div>
                {isEmployerOrAdmin && (
                    <div className="flex flex-wrap gap-1.5">
                        <Button onClick={() => setMigrateOpen(true)} variant="outline" className="rounded-none" data-testid="sc-migrate-btn">
                            <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Migrate jobs & seeds
                        </Button>
                        <Button onClick={() => setBulkAddOpen(true)} variant="outline" className="rounded-none" data-testid="sc-bulk-add-btn">
                            <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Bulk add
                        </Button>
                        <Button onClick={() => setDiscoverOpen(true)} className="rounded-none bg-foreground text-white hover:bg-foreground/90" data-testid="sc-discover-btn">
                            <Globe className="w-3.5 h-3.5 mr-1.5" /> Discover from web
                        </Button>
                        {isAdmin && (
                            <>
                                <Button onClick={() => setAiOpen(true)} className="rounded-none bg-ikb hover:bg-ikb/90" data-testid="sc-ai-btn">
                                    <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI generate more
                                </Button>
                                <Button variant="outline" onClick={() => setAddOpen(true)} className="rounded-none" data-testid="sc-add-skill-btn">
                                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add skill
                                </Button>
                                <Button variant="outline" onClick={() => setAddIndOpen(true)} className="rounded-none" data-testid="sc-add-industry-btn">
                                    <Building2 className="w-3.5 h-3.5 mr-1.5" /> Industry
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Stats strip */}
            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 border border-border bg-white divide-x divide-border" data-testid="sc-stats">
                    <StatCell label="Global skills" value={stats.total_skills} />
                    <StatCell label="Categories" value={categories.length} />
                    <StatCell label="Industries" value={industries.length} />
                    <StatCell label="Selected by us" value={stats.company_selected_count} tint="ikb" />
                </div>
            )}

            {/* Weekly AI Prompt Tuner suggestions (from ai_feedback loop).
                Admins see this even without a company so they can trigger
                cross-tenant runs via the "Run now" button. Employers/
                recruiters only see it when they belong to a company. */}
            {(isAdmin || (hasCompany && isEmployerOrAdmin)) && <PromptTuningCard isAdmin={isAdmin} />}

            {/* View tabs */}
            <div className="border-b border-border flex flex-wrap gap-0.5" data-testid="sc-view-tabs">
                {[
                    { id: "library", label: "Library", icon: Layers },
                    { id: "trending", label: "Trending", icon: TrendingUp },
                    { id: "declining", label: "Declining / obsolete", icon: TrendingDown },
                    { id: "gap", label: "Company vs industry gap", icon: Target },
                ].map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setView(t.id)}
                            data-testid={`sc-tab-${t.id}`}
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                                view === t.id ? "border-ikb text-ikb" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {view === "library" && (
              <>
                {isEmployerOrAdmin && <TrendConfigPanel />}
                {selectedIds.size > 0 && (
                    <div className="border border-ikb bg-ikb/5 px-3 py-2 flex items-center justify-between text-xs" data-testid="sc-selected-bar">
                        <span className="font-mono uppercase tracking-widest text-ikb">{selectedIds.size} selected</span>
                        <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setReclassifyOpen(true)} data-testid="sc-bulk-reclassify-btn">
                                Reclassify…
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setSelectedIds(new Set())} data-testid="sc-clear-selection">
                                <X className="w-3 h-3 mr-1" /> Clear
                            </Button>
                        </div>
                    </div>
                )}
                {/* Two-pane grid */}
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
                {/* LEFT — Industries */}
                <aside className="border border-border bg-white lg:sticky lg:top-4 self-start" data-testid="sc-industries">
                    <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center justify-between">
                        <span>Our industries</span>
                        <span className="text-foreground font-bold">{companyIndustries.length}</span>
                    </div>
                    <div className="p-2 space-y-0.5 max-h-[70vh] overflow-y-auto">
                        {!hasCompany && isEmployerOrAdmin && (
                            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 mb-1" data-testid="sc-no-company-note">
                                Selections are per-company. Your admin account has no company profile, so toggles are view-only here.
                            </div>
                        )}
                        {industries.map((i) => {
                            const on = companyIndustries.includes(i.slug);
                            return (
                                <button
                                    type="button"
                                    key={i.slug}
                                    onClick={() => toggleIndustry(i.slug)}
                                    disabled={!isEmployerOrAdmin}
                                    className={`w-full text-left px-2 py-1.5 border text-xs flex items-center gap-2 transition-colors ${on ? "border-ikb bg-ikb/5 text-ikb font-medium" : "border-transparent hover:border-border"}`}
                                    data-testid={`sc-industry-${i.slug}`}
                                >
                                    {on ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <Circle className="w-3.5 h-3.5 shrink-0 text-foreground/30" />}
                                    <span className="truncate">{i.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                {/* RIGHT — Filters + grid */}
                <div className="space-y-3 min-w-0">
                    <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search skills, tools, certifications…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="rounded-none h-9 pl-8"
                                data-testid="sc-search"
                            />
                        </div>
                        <select
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            className="h-9 px-2 border border-border bg-white text-sm"
                            data-testid="sc-industry-filter"
                        >
                            <option value="">All industries</option>
                            {industries.map((i) => (
                                <option key={i.slug} value={i.slug}>{i.name}</option>
                            ))}
                        </select>
                        <label className="inline-flex items-center gap-1.5 text-xs font-mono">
                            <Checkbox checked={onlyUsed} onCheckedChange={(v) => setOnlyUsed(!!v)} className="rounded-none" data-testid="sc-only-used" />
                            <span>We use only</span>
                        </label>
                        {isAdmin && (
                            <label className="inline-flex items-center gap-1.5 text-xs font-mono">
                                <Checkbox checked={onlyPending} onCheckedChange={(v) => setOnlyPending(!!v)} className="rounded-none" data-testid="sc-only-pending" />
                                <span>Pending approval</span>
                            </label>
                        )}
                        <Button variant="outline" onClick={loadSkills} className="rounded-none h-9" data-testid="sc-refresh"><RefreshCw className={`w-3.5 h-3.5 ${skillsLoading ? "animate-spin" : ""}`} /></Button>
                    </div>

                    {/* Category chips */}
                    <div className="flex flex-wrap gap-1" data-testid="sc-categories">
                        <CategoryChip label="All" testid="sc-cat-all" active={!category} onClick={() => setCategory("")} count={stats?.total_skills} />
                        {categories.map((c) => (
                            <CategoryChip
                                key={c.key}
                                label={c.label}
                                testid={`sc-cat-${c.key}`}
                                active={category === c.key}
                                onClick={() => setCategory(category === c.key ? "" : c.key)}
                                count={catCounts[c.key]}
                                description={c.description}
                            />
                        ))}
                    </div>

                    {/* Skill grid */}
                    <div className="border border-border bg-white min-h-[320px]">
                        <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center justify-between">
                            <span>{total.toLocaleString()} skills {onlyUsed ? "we use" : ""}</span>
                            <span className="text-foreground/60">Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</span>
                        </div>
                        {skillsLoading ? (
                            <div className="p-16 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                        ) : skills.length === 0 ? (
                            <div className="p-12 text-center text-sm text-muted-foreground">No skills match this filter. {isAdmin && "Try 'AI generate more' to expand the catalog."}</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 divide-y divide-border sm:divide-y-0 sm:[&>*:nth-child(-n+2)]:border-b-0" data-testid="sc-skill-grid">
                                {skills.map((s) => (
                                    <SkillCard
                                        key={s.id}
                                        skill={s}
                                        used={companySkillIds.has(s.id)}
                                        onToggle={() => toggleSkill(s)}
                                        onApprove={() => approveSkill(s)}
                                        onDelete={() => deleteSkill(s)}
                                        onEdit={() => setEditSkill(s)}
                                        selected={selectedIds.has(s.id)}
                                        onSelectToggle={() => {
                                            const next = new Set(selectedIds);
                                            if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                                            setSelectedIds(next);
                                        }}
                                        canApprove={isAdmin && !s.is_approved}
                                        canDelete={isAdmin}
                                        canEdit={isEmployerOrAdmin}
                                    />
                                ))}
                            </div>
                        )}
                        {/* Pagination */}
                        {total > pageSize && (
                            <div className="flex items-center justify-between px-3 py-2 border-t border-border">
                                <button type="button" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="text-xs font-mono uppercase tracking-widest hover:text-ikb disabled:opacity-30" data-testid="sc-page-prev">← Prev</button>
                                <span className="text-xs font-mono text-muted-foreground">Page {page}</span>
                                <button type="button" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} className="text-xs font-mono uppercase tracking-widest hover:text-ikb disabled:opacity-30" data-testid="sc-page-next">Next →</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
              </>
            )}

            {view === "trending" && (
                <TrendsPanel status="trending" title="Trending & rising skills" categories={categories} industries={industries} isEmployerOrAdmin={isEmployerOrAdmin} />
            )}
            {view === "declining" && (
                <TrendsPanel status="declining" title="Declining & obsolete skills" categories={categories} industries={industries} isEmployerOrAdmin={isEmployerOrAdmin} />
            )}
            {view === "gap" && (
                <GapReportPanel industries={industries} companyIndustries={companyIndustries} />
            )}

            {/* Dialogs */}
            <AddSkillDialog open={addOpen} onClose={() => setAddOpen(false)} categories={categories} industries={industries} onCreated={() => { setAddOpen(false); loadSkills(); loadStatic(); }} />
            <AIGenerateDialog open={aiOpen} onClose={() => setAiOpen(false)} categories={categories} industries={industries} onDone={() => { setAiOpen(false); loadSkills(); loadStatic(); }} />
            <AddIndustryDialog open={addIndOpen} onClose={() => setAddIndOpen(false)} onCreated={() => { setAddIndOpen(false); loadStatic(); }} />
            <DiscoverDialog open={discoverOpen} onClose={() => setDiscoverOpen(false)} categories={categories} industries={industries} isAdmin={isAdmin} onImported={() => { loadSkills(); loadStatic(); }} />
            <BulkAddDialog open={bulkAddOpen} onClose={() => setBulkAddOpen(false)} categories={categories} industries={industries} onDone={() => { setBulkAddOpen(false); loadSkills(); loadStatic(); }} />
            <MigrateDialog open={migrateOpen} onClose={() => setMigrateOpen(false)} onDone={() => { setMigrateOpen(false); loadSkills(); loadStatic(); }} />
            <EditSkillDialog skill={editSkill} onClose={() => setEditSkill(null)} categories={categories} industries={industries} onSaved={() => { setEditSkill(null); loadSkills(); }} />
            <BulkReclassifyDialog
                open={reclassifyOpen}
                onClose={() => setReclassifyOpen(false)}
                categories={categories}
                industries={industries}
                selectedIds={Array.from(selectedIds)}
                onDone={() => { setReclassifyOpen(false); setSelectedIds(new Set()); loadSkills(); }}
            />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════
function StatCell({ label, value, tint = "muted" }) {
    const tints = { ikb: "text-ikb", muted: "text-foreground" };
    return (
        <div className="p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
            <div className={`font-display font-black text-2xl mt-0.5 ${tints[tint] || tints.muted}`}>{value ?? "—"}</div>
        </div>
    );
}

function CategoryChip({ label, active, onClick, count, description, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={description}
            className={`px-2.5 py-1 border text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${active ? "border-ikb bg-ikb text-white" : "border-border bg-white hover:border-ikb hover:text-ikb"}`}
            data-testid={testid}
        >
            <span>{label}</span>
            {count !== undefined && count !== null && <span className={active ? "text-white/70" : "text-muted-foreground"}>({count})</span>}
        </button>
    );
}

function SkillCard({ skill, used, onToggle, onApprove, onDelete, onEdit, selected, onSelectToggle, canApprove, canDelete, canEdit }) {
    const catTint = {
        technology: "text-indigo-700", framework: "text-violet-700", tool: "text-slate-700",
        software: "text-sky-700", certification: "text-emerald-700", domain: "text-orange-700",
        technical: "text-blue-700", non_technical: "text-teal-700", leadership: "text-amber-700", soft: "text-pink-700",
    }[skill.category] || "text-muted-foreground";
    return (
        <div className={`p-3 border-b sm:border-b border-r border-border relative group ${used ? "bg-ikb/5" : selected ? "bg-ikb/10" : "bg-white hover:bg-secondary/20"} transition-colors`} data-testid={`sc-skill-${skill.id}`}>
            <div className="flex items-start gap-2">
                {onSelectToggle && (
                    <Checkbox
                        checked={!!selected}
                        onCheckedChange={onSelectToggle}
                        className="rounded-none mt-0.5 shrink-0"
                        data-testid={`sc-skill-select-${skill.id}`}
                    />
                )}
                <button
                    type="button"
                    onClick={onToggle}
                    className="mt-0.5 shrink-0"
                    title={used ? "We use this — click to remove" : "Mark as used by our company"}
                    data-testid={`sc-skill-toggle-${skill.id}`}
                >
                    {used ? <CheckCircle2 className="w-5 h-5 text-ikb" /> : <Circle className="w-5 h-5 text-foreground/30 group-hover:text-foreground/60" />}
                </button>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <div className={`font-medium text-sm truncate ${used ? "text-ikb" : ""}`}>{skill.name}</div>
                        {!skill.is_approved && <span className="text-[9px] font-mono uppercase tracking-widest border border-amber-400 bg-amber-50 text-amber-700 px-1">pending</span>}
                        {skill.is_ai_generated && <Sparkles className="w-3 h-3 text-violet-500" title="AI-generated" />}
                        <TrendChip status={skill.trend_status} score={skill.trend_score} manual={!!skill.trend_manual_override} />
                    </div>
                    <div className={`text-[10px] font-mono uppercase tracking-widest ${catTint}`}>{(skill.category || "").replace("_", " ")}</div>
                    {(skill.industry_tags || []).length > 0 && (
                        <div className="text-[10px] text-foreground/50 truncate mt-0.5" title={skill.industry_tags.join(", ")}>
                            {skill.industry_tags.slice(0, 3).join(" · ")}{skill.industry_tags.length > 3 ? ` +${skill.industry_tags.length - 3}` : ""}
                        </div>
                    )}
                    {(skill.aliases || []).length > 0 && (
                        <div className="text-[10px] text-foreground/40 italic mt-0.5 truncate">aka {skill.aliases.join(", ")}</div>
                    )}
                    {skill.notes && (
                        <div className="text-[10px] text-foreground/60 mt-0.5 truncate" title={skill.notes}>📝 {skill.notes}</div>
                    )}
                </div>
                {(canApprove || canDelete || canEdit) && (
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 shrink-0 transition-opacity">
                        {canEdit && (
                            <button type="button" onClick={onEdit} title="Edit" className="p-1 border border-border hover:border-ikb hover:text-ikb" data-testid={`sc-skill-edit-${skill.id}`}>
                                <Pencil className="w-3 h-3" />
                            </button>
                        )}
                        {canApprove && (
                            <button type="button" onClick={onApprove} title="Approve" className="p-1 border border-border hover:border-emerald-500 hover:text-emerald-700" data-testid={`sc-skill-approve-${skill.id}`}>
                                <ShieldCheck className="w-3 h-3" />
                            </button>
                        )}
                        {canDelete && (
                            <button type="button" onClick={onDelete} title="Delete" className="p-1 border border-border hover:border-rose-500 hover:text-rose-700" data-testid={`sc-skill-delete-${skill.id}`}>
                                <Trash2 className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function TrendChip({ status, score, manual }) {
    if (!status) return null;
    const map = {
        trending:  { cls: "border-emerald-500 bg-emerald-50 text-emerald-700", icon: TrendingUp, label: "Trending" },
        rising:    { cls: "border-teal-500 bg-teal-50 text-teal-700", icon: ArrowUpRight, label: "Rising" },
        stable:    { cls: "border-slate-300 bg-slate-50 text-slate-600", icon: Activity, label: "Stable" },
        declining: { cls: "border-amber-500 bg-amber-50 text-amber-700", icon: TrendingDown, label: "Declining" },
        obsolete:  { cls: "border-rose-500 bg-rose-50 text-rose-700", icon: AlertTriangle, label: "Obsolete" },
        unknown:   { cls: "border-border bg-white text-muted-foreground", icon: Activity, label: "Unknown" },
    };
    const v = map[status] || map.unknown;
    const Icon = v.icon;
    return (
        <span className={`inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest border ${v.cls} px-1`} title={manual ? `${v.label} (manual override)` : v.label}>
            <Icon className="w-2.5 h-2.5" /> {v.label}{typeof score === "number" ? ` ${score}` : ""}{manual ? "*" : ""}
        </span>
    );
}

function AddSkillDialog({ open, onClose, categories, industries, onCreated }) {
    const [form, setForm] = useState({ name: "", category: "technology", industry_tags: [], aliases: "" });
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setForm({ name: "", category: "technology", industry_tags: [], aliases: "" }); }, [open]);
    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            await api.post("/skill-center/skills", {
                name: form.name.trim(),
                category: form.category,
                industry_tags: form.industry_tags,
                aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
            });
            toast.success("Skill added");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg rounded-none" data-testid="sc-add-skill-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4 text-ikb" /> Add skill</DialogTitle>
                    <DialogDescription>Add a new item to the global skill library.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Name</label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="sc-add-name" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category</label>
                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-add-cat">
                            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Industries (optional)</label>
                        <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-border p-2">
                            {industries.map((i) => (
                                <label key={i.slug} className="text-xs inline-flex items-center gap-1.5">
                                    <input type="checkbox" checked={form.industry_tags.includes(i.slug)}
                                        onChange={(e) => setForm({ ...form, industry_tags: e.target.checked ? [...form.industry_tags, i.slug] : form.industry_tags.filter((s) => s !== i.slug) })} />
                                    <span className="truncate">{i.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Aliases (comma-separated)</label>
                        <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="e.g. js, ecmascript" className="rounded-none" />
                    </div>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-add-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />} Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AIGenerateDialog({ open, onClose, categories, industries, onDone }) {
    const [category, setCategory] = useState("technical");
    const [industry, setIndustry] = useState("");
    const [count, setCount] = useState(20);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async () => {
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/skill-center/ai-generate", {
                category, industry: industry || null, count, exclude_existing: true,
            });
            setResult(data);
            toast.success(`Added ${data.inserted_count} new pending skills`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg rounded-none" data-testid="sc-ai-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-ikb" /> AI generate skills</DialogTitle>
                    <DialogDescription>Claude Sonnet 4.5 will draft {count} new skills that don&apos;t already exist in the catalog. They land as <b>pending</b> — approve before they&apos;re visible to companies.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-ai-cat">
                            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Industry (optional — leave empty for cross-industry)</label>
                        <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-ai-ind">
                            <option value="">Cross-industry / general</option>
                            {industries.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">How many? (max 40)</label>
                        <Input type="number" value={count} onChange={(e) => setCount(Math.min(40, Math.max(1, Number(e.target.value) || 20)))} className="rounded-none" min={1} max={40} />
                    </div>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-ai-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Generate
                    </Button>
                    {result && (
                        <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs">
                            <div className="font-mono uppercase tracking-widest text-emerald-800 mb-1">Result</div>
                            <div>Inserted: <b>{result.inserted_count}</b> · Skipped as duplicates: <b>{result.duplicates_skipped}</b></div>
                            {result.inserted?.length > 0 && (
                                <ul className="mt-2 list-disc pl-4 max-h-32 overflow-y-auto">
                                    {result.inserted.map((s) => <li key={s.id}>{s.name}</li>)}
                                </ul>
                            )}
                            <Button variant="outline" onClick={onDone} className="rounded-none mt-2 w-full h-8 text-xs">Refresh &amp; close</Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function DiscoverDialog({ open, onClose, categories, industries, isAdmin, onImported }) {
    const [query, setQuery] = useState("");
    const [industry, setIndustry] = useState("");
    const [busy, setBusy] = useState(false);
    const [results, setResults] = useState(null);
    const [checked, setChecked] = useState(new Set());
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);

    useEffect(() => {
        if (open) { setQuery(""); setIndustry(""); setResults(null); setChecked(new Set()); setImportResult(null); }
    }, [open]);

    const search = async () => {
        if (busy) return;
        if (query.trim().length < 3) { toast.error("Enter at least 3 characters"); return; }
        setBusy(true); setResults(null); setImportResult(null);
        try {
            const { data } = await api.post("/skill-center/discover", { query: query.trim(), industry: industry || null, count: 30 });
            setResults(data);
            setChecked(new Set(data.items.filter((i) => !i.exists).map((i) => i.name)));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const toggleRow = (name) => {
        const next = new Set(checked);
        if (next.has(name)) next.delete(name); else next.add(name);
        setChecked(next);
    };

    const setRowCategory = (name, cat) => {
        setResults((cur) => ({ ...cur, items: cur.items.map((i) => (i.name === name ? { ...i, category: cat } : i)) }));
    };

    const doImport = async () => {
        const items = (results?.items || []).filter((i) => !i.exists && checked.has(i.name)).map((i) => ({
            name: i.name, category: i.category, aliases: i.aliases,
            industry_tags: industry ? [industry] : [], popularity_rank: i.popularity_rank,
        }));
        if (items.length === 0) { toast.error("Nothing selected"); return; }
        setImporting(true);
        try {
            const { data } = await api.post("/skill-center/discover/import", { items });
            setImportResult(data);
            const importedNames = new Set(items.map((i) => i.name));
            setResults((cur) => ({ ...cur, items: cur.items.map((i) => (importedNames.has(i.name) ? { ...i, exists: true, existing_name: i.name } : i)) }));
            setChecked(new Set());
            toast.success(`Imported ${data.inserted_count} skill${data.inserted_count === 1 ? "" : "s"}${data.pending_approval ? " (pending admin approval)" : ""}`);
            onImported?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setImporting(false); }
    };

    const newItems = (results?.items || []).filter((i) => !i.exists);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="sc-discover-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Globe className="w-4 h-4 text-ikb" /> Discover skills from the web</DialogTitle>
                    <DialogDescription>
                        Enter keywords (e.g. &quot;SEO tools&quot;, &quot;video editing software&quot;, &quot;supply chain certifications&quot;). We run a live web
                        search, extract real tools &amp; skills, and show which are already in the master vs new.
                        {!isAdmin && <span className="block mt-1 text-amber-700">Skills you import will be pending until an admin approves them.</span>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex gap-2 flex-wrap">
                        <Input
                            placeholder='e.g. "best SEO tools", "warehouse management software"…'
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && !busy && search()}
                            className="rounded-none flex-1 min-w-[220px]"
                            data-testid="sc-discover-query"
                        />
                        <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm" data-testid="sc-discover-industry">
                            <option value="">Tag industry (optional)</option>
                            {industries.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
                        </select>
                        <Button onClick={search} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90" data-testid="sc-discover-search">
                            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />} Search web
                        </Button>
                    </div>

                    {busy && (
                        <div className="p-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" /> Searching the web &amp; extracting skills…
                        </div>
                    )}

                    {results && (
                        <>
                            <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-muted-foreground">
                                <span data-testid="sc-discover-summary">
                                    {results.items.length} found · <span className="text-emerald-700">{results.new_count} new</span> · {results.existing_count} already in master
                                </span>
                                {newItems.length > 0 && (
                                    <button
                                        type="button"
                                        className="hover:text-ikb"
                                        onClick={() => setChecked(checked.size === newItems.length ? new Set() : new Set(newItems.map((i) => i.name)))}
                                        data-testid="sc-discover-toggle-all"
                                    >
                                        {checked.size === newItems.length ? "Unselect all new" : "Select all new"}
                                    </button>
                                )}
                            </div>
                            <div className="border border-border divide-y divide-border max-h-[380px] overflow-y-auto" data-testid="sc-discover-results">
                                {results.items.map((i) => (
                                    <div key={i.name} className={`px-3 py-2 flex items-start gap-2.5 text-sm ${i.exists ? "bg-secondary/30 opacity-70" : "bg-white"}`} data-testid={`sc-discover-row-${i.name.replace(/\s+/g, "-").toLowerCase()}`}>
                                        <Checkbox
                                            checked={!i.exists && checked.has(i.name)}
                                            disabled={i.exists}
                                            onCheckedChange={() => toggleRow(i.name)}
                                            className="rounded-none mt-0.5"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-medium">{i.name}</span>
                                                {i.exists ? (
                                                    <span className="text-[9px] font-mono uppercase tracking-widest border border-border bg-secondary text-muted-foreground px-1" title={i.existing_name !== i.name ? `Matched via alias of "${i.existing_name}"` : undefined}>
                                                        In master{i.existing_name && i.existing_name.toLowerCase() !== i.name.toLowerCase() ? ` as ${i.existing_name}` : ""}
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] font-mono uppercase tracking-widest border border-emerald-400 bg-emerald-50 text-emerald-700 px-1">New</span>
                                                )}
                                            </div>
                                            {i.description && <div className="text-xs text-foreground/60 mt-0.5">{i.description}</div>}
                                            {i.aliases?.length > 0 && <div className="text-[10px] text-foreground/40 italic">aka {i.aliases.join(", ")}</div>}
                                        </div>
                                        {!i.exists && (
                                            <select value={i.category} onChange={(e) => setRowCategory(i.name, e.target.value)} className="h-7 px-1 border border-border bg-white text-xs shrink-0">
                                                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                            </select>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <Button onClick={doImport} disabled={importing || checked.size === 0} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-discover-import">
                                {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                                Add {checked.size} selected to master {!isAdmin && "(pending approval)"}
                            </Button>
                            {importResult && (
                                <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs" data-testid="sc-discover-import-result">
                                    Imported <b>{importResult.inserted_count}</b>{importResult.skipped_count > 0 && <> · skipped {importResult.skipped_count} (already existed)</>}
                                    {importResult.pending_approval && <span className="block text-amber-700 mt-1">Awaiting admin approval before visible to all companies.</span>}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AddIndustryDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", description: "" });
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setForm({ name: "", description: "" }); }, [open]);
    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        setBusy(true);
        try {
            await api.post("/skill-center/industries", form);
            toast.success("Industry added");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md rounded-none" data-testid="sc-add-industry-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4 text-ikb" /> Add industry</DialogTitle>
                    <DialogDescription>Add a new industry to the global taxonomy.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Industry name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="sc-industry-name" />
                    <Input placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" />
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-industry-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />} Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bulk-add dialog — paste raw list, AI cleans up, all land as pending
// ═══════════════════════════════════════════════════════════════════════════
function BulkAddDialog({ open, onClose, categories, industries, onDone }) {
    const [text, setText] = useState("");
    const [category, setCategory] = useState("");
    const [industry, setIndustry] = useState("");
    const [useAI, setUseAI] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (open) { setText(""); setCategory(""); setIndustry(""); setUseAI(true); setResult(null); }
    }, [open]);

    const submit = async () => {
        if (text.trim().length < 2) { toast.error("Paste some skills first"); return; }
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/skill-center/bulk-add", {
                raw_text: text,
                default_category: category || null,
                industry: industry || null,
                use_ai: useAI,
            });
            setResult(data);
            toast.success(`Added ${data.inserted_count} pending skills · ${data.skipped_count} duplicates skipped`);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="sc-bulk-add-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-ikb" /> Bulk add skills</DialogTitle>
                    <DialogDescription>
                        Paste a raw list — one per line, or comma-separated. Claude will clean up naming, dedupe against
                        the master catalog, and classify each into a category. Everything lands in <b>Pending review</b>.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Textarea
                        rows={8}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={"React.js\nnextjs\nPostman\nAWS Lambda, Kubernetes, Docker\nFigma"}
                        className="rounded-none font-mono text-xs"
                        data-testid="sc-bulk-add-textarea"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Default category (optional)</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-bulk-add-cat">
                                <option value="">Let AI decide</option>
                                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Industry (optional)</label>
                            <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-bulk-add-industry">
                                <option value="">Cross-industry</option>
                                {industries.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs">
                        <Checkbox checked={useAI} onCheckedChange={(v) => setUseAI(!!v)} className="rounded-none" data-testid="sc-bulk-add-use-ai" />
                        <span><Sparkles className="w-3 h-3 inline mr-1 text-violet-500" /> Clean up with Claude (recommended)</span>
                    </label>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-bulk-add-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                        Add to pending queue
                    </Button>
                    {result && (
                        <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1" data-testid="sc-bulk-add-result">
                            <div>
                                Parsed <b>{result.raw_count}</b> · Inserted <b>{result.inserted_count}</b> · Skipped <b>{result.skipped_count}</b> {result.used_ai ? "(AI cleanup used)" : "(raw)"}
                            </div>
                            {result.skipped?.length > 0 && (
                                <details>
                                    <summary className="cursor-pointer text-amber-700">Show skipped ({result.skipped.length})</summary>
                                    <ul className="mt-1 pl-4 list-disc max-h-32 overflow-y-auto">
                                        {result.skipped.map((s, i) => <li key={i}><b>{s.name}</b> — {s.reason}</li>)}
                                    </ul>
                                </details>
                            )}
                            {result.inserted?.length > 0 && (
                                <details open>
                                    <summary className="cursor-pointer text-emerald-800">Show inserted ({result.inserted.length})</summary>
                                    <ul className="mt-1 pl-4 list-disc max-h-32 overflow-y-auto">
                                        {result.inserted.map((s) => <li key={s.id}>{s.name} <span className="text-[10px] text-muted-foreground">({s.category})</span></li>)}
                                    </ul>
                                </details>
                            )}
                            <div className="text-amber-700">Awaiting admin approval before visible catalog-wide. Filter <b>Pending approval</b> on the library to review.</div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Edit-skill dialog — name, category, aliases, industries, notes, popularity, trend override
// ═══════════════════════════════════════════════════════════════════════════
function EditSkillDialog({ skill, onClose, categories, industries, onSaved }) {
    const open = !!skill;
    const [form, setForm] = useState({
        name: "", category: "technology", aliases: "", industry_tags: [], notes: "", popularity_rank: 500,
    });
    const [trendStatus, setTrendStatus] = useState("");
    const [trendReason, setTrendReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [refreshingTrend, setRefreshingTrend] = useState(false);

    useEffect(() => {
        if (skill) {
            setForm({
                name: skill.name || "",
                category: skill.category || "technology",
                aliases: (skill.aliases || []).join(", "),
                industry_tags: skill.industry_tags || [],
                notes: skill.notes || "",
                popularity_rank: skill.popularity_rank || 500,
            });
            setTrendStatus(skill.trend_manual_override || "");
            setTrendReason(skill.trend_reason || "");
        }
    }, [skill]);

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name required"); return; }
        setBusy(true);
        try {
            await api.patch(`/skill-center/skills/${skill.id}`, {
                name: form.name.trim(),
                category: form.category,
                aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
                industry_tags: form.industry_tags,
                notes: form.notes,
                popularity_rank: Number(form.popularity_rank) || 500,
            });
            if (trendStatus !== (skill.trend_manual_override || "")) {
                await api.patch(`/skill-center/skills/${skill.id}/trend`, {
                    status: trendStatus || null,
                    reason: trendReason,
                });
            }
            toast.success("Saved");
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const refreshTrend = async () => {
        setRefreshingTrend(true);
        try {
            const { data } = await api.post("/skill-center/trends/analyze", { skill_ids: [skill.id] });
            const row = data.analyzed?.[0];
            if (row) toast.success(`Trend refreshed → ${row.status} (${row.score})`);
            onSaved?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRefreshingTrend(false); }
    };

    if (!skill) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="sc-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-ikb" /> Edit &quot;{skill.name}&quot;</DialogTitle>
                    <DialogDescription>Update the master entry. Changes apply globally.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Name</label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" data-testid="sc-edit-name" />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category</label>
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-edit-category">
                                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Aliases (comma-separated)</label>
                        <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} className="rounded-none" data-testid="sc-edit-aliases" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Industries</label>
                        <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border border-border p-2">
                            {industries.map((i) => (
                                <label key={i.slug} className="text-xs inline-flex items-center gap-1.5">
                                    <input
                                        type="checkbox"
                                        checked={form.industry_tags.includes(i.slug)}
                                        onChange={(e) => setForm({
                                            ...form,
                                            industry_tags: e.target.checked
                                                ? [...form.industry_tags, i.slug]
                                                : form.industry_tags.filter((s) => s !== i.slug),
                                        })}
                                    />
                                    <span className="truncate">{i.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notes</label>
                        <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal note about this skill…" className="rounded-none text-xs" data-testid="sc-edit-notes" />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Popularity rank (1 = universal, 900 = niche)</label>
                        <Input type="number" min={1} max={1000} value={form.popularity_rank} onChange={(e) => setForm({ ...form, popularity_rank: e.target.value })} className="rounded-none" />
                    </div>
                    <div className="border border-border p-2 bg-secondary/20">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex items-center justify-between">
                            <span>Trend status</span>
                            <button
                                type="button"
                                onClick={refreshTrend}
                                disabled={refreshingTrend}
                                className="text-ikb hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                                data-testid="sc-edit-refresh-trend"
                            >
                                {refreshingTrend ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                Refresh from web
                            </button>
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                            {[
                                { v: "", label: "Auto (AI)" },
                                { v: "trending", label: "Trending" },
                                { v: "rising", label: "Rising" },
                                { v: "stable", label: "Stable" },
                                { v: "declining", label: "Declining" },
                                { v: "obsolete", label: "Obsolete" },
                            ].map((o) => (
                                <button
                                    key={o.v || "auto"}
                                    type="button"
                                    onClick={() => setTrendStatus(o.v)}
                                    data-testid={`sc-edit-trend-${o.v || "auto"}`}
                                    className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${trendStatus === o.v ? "border-ikb bg-ikb text-white" : "border-border bg-white hover:border-ikb"}`}
                                >{o.label}</button>
                            ))}
                        </div>
                        {skill.trend_status && !trendStatus && (
                            <div className="text-[10px] text-muted-foreground mt-1">Current auto: <b>{skill.trend_status}</b>{skill.trend_score !== undefined ? ` · score ${skill.trend_score}` : ""}</div>
                        )}
                        <Input
                            className="rounded-none mt-2 text-xs"
                            placeholder="Reason (optional)"
                            value={trendReason}
                            onChange={(e) => setTrendReason(e.target.value)}
                            data-testid="sc-edit-trend-reason"
                        />
                        {skill.trend_sources?.length > 0 && (
                            <details className="mt-2 text-[10px]">
                                <summary className="cursor-pointer text-muted-foreground">Web sources ({skill.trend_sources.length})</summary>
                                <ul className="mt-1 pl-3 list-disc">
                                    {skill.trend_sources.map((s, i) => (
                                        <li key={i}><a href={s.url} target="_blank" rel="noreferrer noopener" className="text-ikb hover:underline">{s.title || s.url}</a></li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                    <Button onClick={save} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-edit-save">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                        Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Bulk-reclassify dialog
// ═══════════════════════════════════════════════════════════════════════════
function BulkReclassifyDialog({ open, onClose, categories, industries, selectedIds, onDone }) {
    const [category, setCategory] = useState("");
    const [addTags, setAddTags] = useState([]);
    const [removeTags, setRemoveTags] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) { setCategory(""); setAddTags([]); setRemoveTags([]); }
    }, [open]);

    const submit = async () => {
        if (!category && addTags.length === 0 && removeTags.length === 0) {
            toast.error("Nothing to change"); return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/skill-center/skills/bulk-reclassify", {
                skill_ids: selectedIds,
                category: category || null,
                add_industry_tags: addTags,
                remove_industry_tags: removeTags,
            });
            toast.success(`Updated ${data.updated} of ${data.requested} skills`);
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg rounded-none" data-testid="sc-reclassify-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-ikb" /> Reclassify {selectedIds.length} skills</DialogTitle>
                    <DialogDescription>Change category and/or industry tags in bulk.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change category (optional)</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm w-full" data-testid="sc-reclassify-cat">
                            <option value="">Don&apos;t change</option>
                            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Add industry tags</label>
                        <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto border border-border p-2">
                            {industries.map((i) => (
                                <label key={i.slug} className="text-xs inline-flex items-center gap-1.5">
                                    <input type="checkbox" checked={addTags.includes(i.slug)} onChange={(e) => setAddTags(e.target.checked ? [...addTags, i.slug] : addTags.filter((s) => s !== i.slug))} />
                                    <span className="truncate">{i.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Remove industry tags</label>
                        <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto border border-border p-2">
                            {industries.map((i) => (
                                <label key={i.slug} className="text-xs inline-flex items-center gap-1.5">
                                    <input type="checkbox" checked={removeTags.includes(i.slug)} onChange={(e) => setRemoveTags(e.target.checked ? [...removeTags, i.slug] : removeTags.filter((s) => s !== i.slug))} />
                                    <span className="truncate">{i.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-ikb/90 w-full" data-testid="sc-reclassify-submit">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                        Apply to {selectedIds.length} skills
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Trends panel — trending / declining views with analyze button
// ═══════════════════════════════════════════════════════════════════════════
function TrendsPanel({ status, title, categories, industries, isEmployerOrAdmin }) {
    const isDeclining = status === "declining";

    const [items, setItems] = useState([]);
    const [buckets, setBuckets] = useState(null);
    const [industry, setIndustry] = useState("");
    const [category, setCategory] = useState("");
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (industry) params.industry = industry;
            if (category) params.category = category;
            const { data } = await api.get("/skill-center/trends", { params });
            setItems(data.items || []);
            setBuckets(data.buckets || null);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [industry, category]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        if (isDeclining) return items.filter((i) => ["declining", "obsolete"].includes(i.trend_status));
        return items.filter((i) => ["trending", "rising"].includes(i.trend_status));
    }, [items, isDeclining]);

    const runAnalyze = async () => {
        setRunning(true);
        try {
            const payload = { limit: 30 };
            if (industry) payload.industry = industry;
            if (category) payload.category = category;
            const { data } = await api.post("/skill-center/trends/analyze", payload);
            toast.success(`Analyzed ${data.count} skills`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRunning(false); }
    };

    return (
        <div className="space-y-3" data-testid={`sc-trends-panel-${status}`}>
            <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-black text-lg mr-2">{title}</h2>
                <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm">
                    <option value="">All industries</option>
                    {industries.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
                </select>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm">
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <Button variant="outline" className="rounded-none h-9" onClick={load} data-testid="sc-trends-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
                {isEmployerOrAdmin && (
                    <Button onClick={runAnalyze} disabled={running} className="rounded-none bg-ikb hover:bg-ikb/90 h-9 ml-auto" data-testid="sc-trends-analyze">
                        {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                        Analyze from web (30 skills)
                    </Button>
                )}
            </div>

            {buckets && (
                <div className="grid grid-cols-2 sm:grid-cols-5 border border-border bg-white divide-x divide-border text-center" data-testid="sc-trends-buckets">
                    {[
                        { k: "trending", label: "Trending", color: "text-emerald-700" },
                        { k: "rising", label: "Rising", color: "text-teal-700" },
                        { k: "stable", label: "Stable", color: "text-slate-600" },
                        { k: "declining", label: "Declining", color: "text-amber-700" },
                        { k: "obsolete", label: "Obsolete", color: "text-rose-700" },
                    ].map((b) => (
                        <div key={b.k} className="p-3">
                            <div className={`font-display font-black text-2xl ${b.color}`} data-testid={`sc-trends-bucket-${b.k}`}>{buckets[b.k] || 0}</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{b.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="border border-border bg-white p-16 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="border border-border bg-white p-12 text-center text-sm text-muted-foreground">
                    No skills yet in this bucket. Click <b>Analyze from web</b> to have AI score the top skills.
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border" data-testid="sc-trends-list">
                    {filtered.map((s) => (
                        <TrendRow key={s.id} skill={s} />
                    ))}
                </div>
            )}
        </div>
    );
}

function TrendRow({ skill }) {
    return (
        <div className="px-3 py-2 flex items-start gap-3" data-testid={`sc-trend-row-${skill.id}`}>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm">{skill.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{(skill.category || "").replace("_", " ")}</span>
                    <TrendChip status={skill.trend_status} score={skill.trend_score} manual={!!skill.trend_manual_override} />
                </div>
                {skill.trend_reason && <div className="text-xs text-foreground/70 mt-0.5">{skill.trend_reason}</div>}
                {skill.trend_replaced_by && (
                    <div className="text-[10px] text-rose-700 mt-0.5">
                        <ArrowUpRight className="w-3 h-3 inline" /> Replaced by <b>{skill.trend_replaced_by}</b>
                    </div>
                )}
                {skill.trend_sources?.length > 0 && (
                    <details className="mt-1 text-[10px]">
                        <summary className="cursor-pointer text-muted-foreground">Sources ({skill.trend_sources.length})</summary>
                        <ul className="mt-1 pl-3 list-disc">
                            {skill.trend_sources.map((s, i) => (
                                <li key={i}><a href={s.url} target="_blank" rel="noreferrer noopener" className="text-ikb hover:underline">{s.title || s.url}</a></li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
            {skill.trend_updated_at && (
                <span className="text-[10px] font-mono text-muted-foreground shrink-0">{new Date(skill.trend_updated_at).toLocaleDateString()}</span>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Company vs Industry Skill Gap panel
// ═══════════════════════════════════════════════════════════════════════════
function GapReportPanel({ industries, companyIndustries }) {
    const initial = companyIndustries[0] || industries[0]?.slug || "";
    const [industry, setIndustry] = useState(initial);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => { setIndustry(companyIndustries[0] || industries[0]?.slug || ""); }, [companyIndustries, industries]);

    const load = useCallback(async () => {
        if (!industry) return;
        setLoading(true);
        try {
            const { data } = await api.get("/skill-center/gap-report", { params: { industry } });
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [industry]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3" data-testid="sc-gap-panel">
            <div className="border border-border bg-white p-3 flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-black text-lg mr-2">Company vs Industry — skill gap</h2>
                <select value={industry} onChange={(e) => setIndustry(e.target.value)} className="h-9 px-2 border border-border bg-white text-sm" data-testid="sc-gap-industry">
                    {industries.map((i) => (
                        <option key={i.slug} value={i.slug}>{i.name}{companyIndustries.includes(i.slug) ? " (yours)" : ""}</option>
                    ))}
                </select>
                <Button variant="outline" className="rounded-none h-9" onClick={load} data-testid="sc-gap-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </Button>
            </div>

            {loading && <div className="border border-border bg-white p-16 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Building report…</div>}
            {data && !loading && <GapReportBody data={data} onSkillsAdopted={load} />}
        </div>
    );
}

// R20 — one row per missing skill with an inline checkbox. Clicking anywhere
// on the pill toggles selection so recruiters don't need to hit the tiny box.
// R22 — Info button reveals a portal-rendered evidence popover fetched from
// /skill-center/skills/{id}/evidence (DDG sources + first-seen date).
function GapSkillPill({ skill, tone = "neutral", checked, onToggle }) {
    const toneCls =
        tone === "emerald" ? "bg-white border-emerald-500 text-emerald-900" :
        "bg-secondary/40 border-border";
    const [evidenceOpen, setEvidenceOpen] = useState(false);
    return (
        <span className="relative inline-flex items-center gap-0.5">
            <label
                className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-xs cursor-pointer select-none hover:border-ikb ${toneCls} ${checked ? "ring-1 ring-ikb" : ""}`}
                data-testid={`sc-gap-pill-${skill.id}`}
            >
                <input
                    type="checkbox"
                    className="h-3 w-3 accent-ikb cursor-pointer"
                    checked={!!checked}
                    onChange={onToggle}
                    data-testid={`sc-gap-pill-cb-${skill.id}`}
                    aria-label={`Select ${skill.name} to add to use list`}
                />
                <span>{skill.name}</span>
                {skill.trend_status && <TrendChip status={skill.trend_status} score={skill.trend_score} />}
            </label>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setEvidenceOpen(true); }}
                className="text-[10px] font-mono uppercase tracking-widest text-foreground/40 hover:text-ikb"
                data-testid={`sc-gap-pill-why-${skill.id}`}
                aria-label={`See why ${skill.name} is trending`}
                title="Why?"
            >
                Why?
            </button>
            {evidenceOpen && (
                <SkillEvidencePopover
                    skillId={skill.id}
                    skillName={skill.name}
                    onClose={() => setEvidenceOpen(false)}
                />
            )}
        </span>
    );
}


// Portal-rendered popover fetching /skill-center/skills/{id}/evidence.
// Renders top 3 sources + AI reason + first-seen date so recruiters can
// verify a skill is genuinely trending before adopting it.
function SkillEvidencePopover({ skillId, skillName, onClose }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/skill-center/skills/${skillId}/evidence`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [skillId]);
    // Close on Esc + click-outside (via backdrop).
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div
            className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="sc-gap-evidence-modal"
        >
            <div className="bg-white border border-border w-full max-w-md max-h-[80vh] overflow-y-auto">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-ikb">Why {skillName}?</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="ml-auto text-foreground/60 hover:text-foreground"
                        aria-label="Close"
                        data-testid="sc-gap-evidence-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-4 space-y-3">
                    {loading && <div className="text-sm text-muted-foreground">Loading evidence…</div>}
                    {err && <div className="text-sm text-rose-700">{err}</div>}
                    {!loading && !err && data && !data.has_evidence && (
                        <div className="text-sm text-muted-foreground">
                            No web evidence has been captured for this skill yet. The next Trend Center refresh will populate DuckDuckGo sources.
                        </div>
                    )}
                    {!loading && !err && data?.has_evidence && (
                        <>
                            <div className="flex items-center gap-2 text-[11px] font-mono">
                                {data.trend_status && <TrendChip status={data.trend_status} score={data.trend_score} />}
                                {data.trend_updated_at && (
                                    <span className="text-foreground/60">
                                        First seen: {new Date(data.trend_updated_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                                    </span>
                                )}
                                {typeof data.trend_confidence === "number" && (
                                    <span className="text-foreground/60">· confidence {(data.trend_confidence * 100).toFixed(0)}%</span>
                                )}
                            </div>
                            {data.reason && (
                                <blockquote className="border-l-2 border-ikb pl-3 text-sm text-foreground/85 italic">
                                    {data.reason}
                                </blockquote>
                            )}
                            {(data.sources || []).length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1.5">Web sources ({data.sources.length})</div>
                                    <ul className="space-y-1.5">
                                        {data.sources.map((src, idx) => (
                                            <li key={src.url} className="text-xs" data-testid={`sc-gap-evidence-source-${idx}`}>
                                                <a
                                                    href={src.url}
                                                    target="_blank"
                                                    rel="noreferrer noopener"
                                                    className="text-ikb hover:underline break-all"
                                                >
                                                    {src.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {data.trend_replaced_by && (
                                <div className="text-[11px] text-amber-700 border border-amber-300 bg-amber-50 px-2 py-1">
                                    Modern replacement: <span className="font-mono">{data.trend_replaced_by}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export function GapReportBody({ data, onSkillsAdopted }) {
    const s = data.summary || {};
    // R20 — bulk-adopt UX. Each missing skill has a checkbox; a floating action
    // bar shows N-selected + "Add to my use list" button. Adds via existing
    // PUT /company/skill-selections {add:[...]} endpoint.
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);
    const toggle = (id) => setSelected((cur) => {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const clearSelection = () => setSelected(new Set());
    // Bulk select helpers per section
    const selectAll = (arr) => setSelected((cur) => {
        const next = new Set(cur);
        arr.forEach((sk) => next.add(sk.id));
        return next;
    });
    const deselectAll = (arr) => setSelected((cur) => {
        const next = new Set(cur);
        arr.forEach((sk) => next.delete(sk.id));
        return next;
    });

    const submit = async () => {
        if (selected.size === 0) return;
        setBusy(true);
        try {
            const res = await api.put("/company/skill-selections", { add: Array.from(selected), remove: [] });
            toast.success(`Added ${selected.size} skill${selected.size === 1 ? "" : "s"} to your use list (total: ${res.data.count})`);
            clearSelection();
            if (onSkillsAdopted) onSkillsAdopted(res.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="sc-gap-body">
            <div className="grid grid-cols-2 sm:grid-cols-5 border border-border bg-white divide-x divide-border">
                <KPI label="Industry skills" value={s.industry_total} />
                <KPI label="You use" value={s.company_uses_total} />
                <KPI label="Coverage" value={`${s.coverage_pct}%`} tint="ikb" />
                <KPI label="Missing" value={s.missing} tint="amber" />
                <KPI label="Declining you use" value={s.declining_used} tint="rose" />
            </div>

            {(data.trending_missing || []).length > 0 && (
                <div className="border border-emerald-300 bg-emerald-50 p-3" data-testid="sc-gap-trending-missing">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-800 flex items-center gap-1.5 flex-1 min-w-0">
                            <TrendingUp className="w-3.5 h-3.5" /> Trending / rising in this industry but you don&apos;t use these yet ({data.trending_missing.length})
                        </div>
                        <button
                            type="button"
                            onClick={() => selectAll(data.trending_missing)}
                            className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 hover:underline"
                            data-testid="sc-gap-select-all-trending"
                        >
                            Select all
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {data.trending_missing.map((sk) => (
                            <GapSkillPill key={sk.id} skill={sk} tone="emerald" checked={selected.has(sk.id)} onToggle={() => toggle(sk.id)} />
                        ))}
                    </div>
                </div>
            )}

            {(data.declining_used || []).length > 0 && (
                <div className="border border-rose-300 bg-rose-50 p-3" data-testid="sc-gap-declining-used">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-rose-800 mb-2 flex items-center gap-1.5">
                        <TrendingDown className="w-3.5 h-3.5" /> You currently use these declining / obsolete skills ({data.declining_used.length})
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {data.declining_used.map((sk) => (
                            <span key={sk.id} className="inline-flex items-center gap-1 bg-white border border-rose-500 text-rose-900 px-2 py-0.5 text-xs">
                                {sk.name}
                                <TrendChip status={sk.trend_status} score={sk.trend_score} />
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <div className="border border-border bg-white">
                <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                    Missing industry skills — grouped by category
                </div>
                {Object.entries(data.missing_by_category || {}).length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">You&apos;re covering every skill in this industry — great work!</div>
                ) : (
                    <div className="divide-y divide-border">
                        {Object.entries(data.missing_by_category || {}).map(([cat, arr]) => {
                            const visible = arr.slice(0, 40);
                            const allSelected = visible.length > 0 && visible.every((sk) => selected.has(sk.id));
                            return (
                                <div key={cat} className="p-3">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex-1 min-w-0">
                                            {cat.replace("_", " ")} · {arr.length}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => (allSelected ? deselectAll(visible) : selectAll(visible))}
                                            className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline"
                                            data-testid={`sc-gap-toggle-cat-${cat}`}
                                        >
                                            {allSelected ? "Deselect visible" : "Select visible"}
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {visible.map((sk) => (
                                            <GapSkillPill key={sk.id} skill={sk} tone="neutral" checked={selected.has(sk.id)} onToggle={() => toggle(sk.id)} />
                                        ))}
                                        {arr.length > 40 && <span className="text-xs text-muted-foreground">+{arr.length - 40} more</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Sticky action bar — visible only when 1+ skill is checked */}
            {selected.size > 0 && (
                <div
                    className="sticky bottom-4 z-10 border border-ikb bg-white shadow-lg p-3 flex items-center gap-3"
                    data-testid="sc-gap-bulk-adopt-bar"
                >
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{selected.size} skill{selected.size === 1 ? "" : "s"} selected</div>
                        <div className="text-[11px] text-muted-foreground">Will be added to your company&apos;s use list — used by JD authoring, sourcing suggestions, and trend digests.</div>
                    </div>
                    <button
                        type="button"
                        onClick={clearSelection}
                        disabled={busy}
                        className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest hover:border-rose-500 hover:text-rose-700 disabled:opacity-40"
                        data-testid="sc-gap-bulk-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#001f7a] disabled:opacity-50"
                        data-testid="sc-gap-bulk-adopt-submit"
                    >
                        {busy ? "Adding…" : `Add ${selected.size} to my use list`}
                    </button>
                </div>
            )}

            {(data.used_not_in_industry || []).length > 0 && (
                <div className="border border-border bg-white">
                    <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                        You use these but they aren&apos;t tagged for this industry ({data.used_not_in_industry.length})
                    </div>
                    <div className="p-3 flex flex-wrap gap-1">
                        {data.used_not_in_industry.map((s) => (
                            <span key={s.id} className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs">{s.name}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function KPI({ label, value, tint = "muted" }) {
    const tints = { ikb: "text-ikb", amber: "text-amber-700", rose: "text-rose-700", muted: "text-foreground" };
    return (
        <div className="p-3 text-center">
            <div className={`font-display font-black text-2xl ${tints[tint] || tints.muted}`}>{value ?? "—"}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
        </div>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
// Trend Config Panel — collapsible admin card at top of Library
// ═══════════════════════════════════════════════════════════════════════════
export function TrendConfigPanel() {
    const [open, setOpen] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [c, s] = await Promise.all([
                    api.get("/skill-center/config"),
                    api.get("/skill-center/trends/cron-status"),
                ]);
                setCfg(c.data.config);
                setStatus(s.data);
            } catch (err) { /* ignore silently */ }
        })();
    }, []);

    const patch = async (updates) => {
        setSaving(true);
        try {
            const { data } = await api.patch("/skill-center/config", updates);
            setCfg(data.config);
            toast.success("Trend settings saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    if (!cfg) return null;

    return (
        <div className="border border-border bg-white" data-testid="sc-trend-config">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-secondary/30"
                data-testid="sc-trend-config-toggle"
            >
                <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-ikb" />
                    <span className="text-[11px] font-mono uppercase tracking-widest">Trend-aware settings</span>
                    <span className="text-[10px] text-muted-foreground">
                        JD bias · {cfg.jd_generation_bias_required ? "on" : "off"} ·
                        boost +{cfg.talent_search_ahead_boost} · penalty −{cfg.talent_search_dated_penalty}
                    </span>
                </div>
                <span className="text-[10px] font-mono text-ikb">{open ? "− Hide" : "+ Show"}</span>
            </button>
            {open && (
                <div className="p-4 border-t border-border grid sm:grid-cols-2 gap-4 text-xs">
                    <ConfigToggle
                        label="Bias AI JD toward trending skills"
                        sub="Applies only to required_skills; nice-to-haves stay neutral."
                        value={cfg.jd_generation_bias_required}
                        onChange={(v) => patch({ jd_generation_bias_required: v })}
                        testid="sc-cfg-jd-bias"
                    />
                    <div className="space-y-1" data-testid="sc-cfg-ahead-boost-wrap">
                        <div className="flex items-center justify-between">
                            <label className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">
                                Talent search: ahead-of-curve boost
                            </label>
                            <span className="text-ikb font-bold" data-testid="sc-cfg-ahead-boost-val">+{cfg.talent_search_ahead_boost}</span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={50}
                            value={cfg.talent_search_ahead_boost}
                            disabled={saving}
                            onChange={(e) => setCfg({ ...cfg, talent_search_ahead_boost: Number(e.target.value) })}
                            onMouseUp={(e) => patch({ talent_search_ahead_boost: Number(e.target.value) })}
                            onKeyUp={(e) => patch({ talent_search_ahead_boost: Number(e.target.value) })}
                            onTouchEnd={(e) => patch({ talent_search_ahead_boost: Number(e.target.value) })}
                            className="w-full"
                            data-testid="sc-cfg-ahead-boost"
                        />
                        <div className="text-[10px] text-muted-foreground">Points added per trending skill the candidate lists that your company hasn&apos;t adopted.</div>
                    </div>
                    <div className="space-y-1" data-testid="sc-cfg-dated-penalty-wrap">
                        <div className="flex items-center justify-between">
                            <label className="font-mono uppercase tracking-widest text-[10px] text-muted-foreground">
                                Talent search: dated-stack penalty
                            </label>
                            <span className="text-rose-700 font-bold" data-testid="sc-cfg-dated-penalty-val">−{cfg.talent_search_dated_penalty}</span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={50}
                            value={cfg.talent_search_dated_penalty}
                            disabled={saving}
                            onChange={(e) => setCfg({ ...cfg, talent_search_dated_penalty: Number(e.target.value) })}
                            onMouseUp={(e) => patch({ talent_search_dated_penalty: Number(e.target.value) })}
                            onKeyUp={(e) => patch({ talent_search_dated_penalty: Number(e.target.value) })}
                            onTouchEnd={(e) => patch({ talent_search_dated_penalty: Number(e.target.value) })}
                            className="w-full"
                            data-testid="sc-cfg-dated-penalty"
                        />
                        <div className="text-[10px] text-muted-foreground">Points subtracted per declining/obsolete skill on the candidate&apos;s resume.</div>
                    </div>
                    <ConfigToggle
                        label="Nightly auto-refresh trends"
                        sub={`Re-analyze skills last touched >${cfg.trend_refresh_days} days ago, up to ${cfg.trend_refresh_limit}/night.`}
                        value={cfg.trend_refresh_enabled}
                        onChange={(v) => patch({ trend_refresh_enabled: v })}
                        testid="sc-cfg-refresh-enabled"
                    />
                    {status && (
                        <div className="sm:col-span-2 border-t border-border pt-3 mt-1 text-[10px] font-mono text-muted-foreground" data-testid="sc-cron-status">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                <span>Last catalog trend update: <b className="text-foreground">{status.last_run_at ? new Date(status.last_run_at).toLocaleString() : "—"}</b></span>
                                <span>Skills tagged so far: <b className="text-foreground">{status.total_tagged || 0}</b></span>
                                <span>Manual refreshes left this hour: <b className={status.manual_hits_remaining > 0 ? "text-emerald-700" : "text-rose-700"}>{status.manual_hits_remaining}</b>/3</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <SeedDriftPanel />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase S5 — Seed drift panel (jobs whose skills have diverged from parent seed)
// ═══════════════════════════════════════════════════════════════════════════
function SeedDriftPanel() {
    const [seeds, setSeeds] = useState([]);
    const [drift, setDrift] = useState({});   // {seed_id: {jobs: [...]}}
    const [expanded, setExpanded] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);   // {seedId, jobId, name}

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/seeds", { params: { active_only: true } });
                setSeeds(data.items || []);
            } catch (err) { /* silent */ }
        })();
    }, []);

    const loadDrift = async (seedId) => {
        if (drift[seedId]) return;
        try {
            const { data } = await api.get(`/skill-center/seeds/${seedId}/job-diff`);
            setDrift((d) => ({ ...d, [seedId]: data }));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (seeds.length === 0) return null;

    return (
        <div className="border-t border-border p-4 space-y-2" data-testid="sc-seed-drift">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Layers className="w-3 h-3" /> JD seeds — jobs that drifted from the template
            </div>
            <div className="space-y-1">
                {seeds.map((s) => {
                    const d = drift[s.id];
                    const jobsWithDrift = (d?.jobs || []).length;
                    return (
                        <div key={s.id} className="border border-border bg-white">
                            <button
                                type="button"
                                onClick={async () => { setExpanded(expanded === s.id ? null : s.id); await loadDrift(s.id); }}
                                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-secondary/30"
                                data-testid={`sc-seed-${s.id}`}
                            >
                                <span className="text-sm">{s.name}</span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                    {d ? `${jobsWithDrift} jobs drifted` : "click to check"}
                                </span>
                            </button>
                            {expanded === s.id && d && (
                                <div className="border-t border-border p-2 space-y-1">
                                    {(d.jobs || []).length === 0 && (
                                        <div className="text-xs text-emerald-700 px-2 py-1">All jobs are in sync with this seed ✓</div>
                                    )}
                                    {(d.jobs || []).map((row) => (
                                        <button
                                            key={row.job.id}
                                            type="button"
                                            onClick={() => setSelectedJob({ seedId: s.id, jobId: row.job.id, name: row.job.title })}
                                            className="w-full flex items-center gap-2 text-left px-2 py-1 hover:bg-secondary/20"
                                            data-testid={`sc-seed-drift-job-${row.job.id}`}
                                        >
                                            <span className="text-xs flex-1 truncate">{row.job.title}</span>
                                            {Object.entries(row.counts || {}).map(([k, v]) => (
                                                v.added || v.removed ? (
                                                    <span key={k} className="text-[10px] font-mono border border-amber-400 bg-amber-50 px-1">
                                                        {k.replace(/_/g, " ")}: +{v.added} / −{v.removed}
                                                    </span>
                                                ) : null
                                            ))}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <SeedDiffModal
                seedId={selectedJob?.seedId}
                jobId={selectedJob?.jobId}
                jobName={selectedJob?.name}
                onClose={() => setSelectedJob(null)}
                onApplied={() => {
                    // Invalidate diff cache for this seed so next open re-fetches
                    if (selectedJob) {
                        setDrift((d) => { const c = { ...d }; delete c[selectedJob.seedId]; return c; });
                    }
                    setSelectedJob(null);
                }}
            />
        </div>
    );
}

function SeedDiffModal({ seedId, jobId, jobName, onClose, onApplied }) {
    const open = !!(seedId && jobId);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selected, setSelected] = useState({}); // {field: {add: Set, drop: Set}}

    useEffect(() => {
        if (!open) { setData(null); setSelected({}); return; }
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/skill-center/seeds/${seedId}/job-diff`, { params: { job_id: jobId } });
                setData(data);
                // Pre-select all seed-additions and drops of red_flags (safe defaults)
                const init = {};
                for (const [f, d] of Object.entries(data.diff || {})) {
                    init[f] = { add: new Set(d.added || []), drop: new Set() };
                }
                setSelected(init);
            } catch (err) { toast.error(formatApiError(err)); }
            finally { setLoading(false); }
        })();
    }, [open, seedId, jobId]);

    const toggle = (field, kind, name) => {
        setSelected((s) => {
            const cur = { ...(s[field] || { add: new Set(), drop: new Set() }) };
            const set = new Set(cur[kind]);
            if (set.has(name)) set.delete(name);
            else set.add(name);
            cur[kind] = set;
            return { ...s, [field]: cur };
        });
    };

    const apply = async () => {
        setApplying(true);
        try {
            const payload = {};
            for (const [f, sel] of Object.entries(selected)) {
                if ((sel.add?.size || 0) > 0 || (sel.drop?.size || 0) > 0) {
                    payload[f] = { add: Array.from(sel.add || []), drop: Array.from(sel.drop || []) };
                }
            }
            if (Object.keys(payload).length === 0) {
                toast.info("Nothing selected");
                setApplying(false);
                return;
            }
            const { data } = await api.post(`/skill-center/jobs/${jobId}/apply-seed-diff`, { apply: payload });
            toast.success(`Applied ${Object.keys(data.applied || {}).length} field(s)`);
            onApplied?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setApplying(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="sc-seed-diff-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Layers className="w-4 h-4 text-ikb" /> Sync &quot;{jobName}&quot; with the seed</DialogTitle>
                    <DialogDescription>
                        Pick which changes to apply. <b>+ Add</b> means the seed has a skill this job lacks;
                        <b> − Remove</b> means this job has extras the seed doesn&apos;t.
                    </DialogDescription>
                </DialogHeader>
                {loading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
                {data && (
                    <div className="space-y-3">
                        {Object.entries(data.diff || {}).map(([field, d]) => {
                            const hasChanges = (d.added?.length || 0) > 0 || (d.removed?.length || 0) > 0;
                            if (!hasChanges) return null;
                            const sel = selected[field] || { add: new Set(), drop: new Set() };
                            return (
                                <div key={field} className="border border-border bg-white p-2" data-testid={`sc-diff-field-${field}`}>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{field.replace(/_/g, " ")}</div>
                                    {d.added?.length > 0 && (
                                        <div className="mb-2">
                                            <div className="text-[10px] text-emerald-700 mb-1">+ Add from seed</div>
                                            <div className="flex flex-wrap gap-1">
                                                {d.added.map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        onClick={() => toggle(field, "add", n)}
                                                        className={`text-xs border px-1.5 py-0.5 ${sel.add?.has(n) ? "border-emerald-500 bg-emerald-100 text-emerald-900" : "border-border bg-white text-muted-foreground"}`}
                                                        data-testid={`sc-diff-add-${field}-${n.replace(/\s+/g,"-").toLowerCase()}`}
                                                    >
                                                        {sel.add?.has(n) ? "+ " : ""}{n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {d.removed?.length > 0 && (
                                        <div>
                                            <div className="text-[10px] text-rose-700 mb-1">− Remove from job (not in seed)</div>
                                            <div className="flex flex-wrap gap-1">
                                                {d.removed.map((n) => (
                                                    <button
                                                        key={n}
                                                        type="button"
                                                        onClick={() => toggle(field, "drop", n)}
                                                        className={`text-xs border px-1.5 py-0.5 ${sel.drop?.has(n) ? "border-rose-500 bg-rose-100 text-rose-900" : "border-border bg-white text-muted-foreground"}`}
                                                        data-testid={`sc-diff-drop-${field}-${n.replace(/\s+/g,"-").toLowerCase()}`}
                                                    >
                                                        {sel.drop?.has(n) ? "− " : ""}{n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {!data.has_changes && (
                            <div className="border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">✓ This job is already in sync with the seed.</div>
                        )}
                        <Button onClick={apply} disabled={applying || !data.has_changes} className="rounded-none bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="sc-diff-apply-btn">
                            {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                            Apply selected changes
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}


function ConfigToggle({ label, sub, value, onChange, testid }) {
    return (
        <label className="flex items-start gap-2 cursor-pointer" data-testid={testid}>
            <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 rounded-none"
            />
            <div>
                <div className="font-medium">{label}</div>
                {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
            </div>
        </label>
    );
}


// ═══════════════════════════════════════════════════════════════════════════
// Phase S3 — Migration dialog (preview + run)
// ═══════════════════════════════════════════════════════════════════════════
function MigrateDialog({ open, onClose, onDone }) {
    const [scope, setScope] = useState("all");
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [running, setRunning] = useState(false);
    const [addUnmatched, setAddUnmatched] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => { if (open) { setPreview(null); setResult(null); } }, [open]);

    const runPreview = async () => {
        setPreviewing(true); setResult(null);
        try {
            const { data } = await api.post("/skill-center/migrate/preview", { scope });
            setPreview(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPreviewing(false); }
    };

    const runMigrate = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/skill-center/migrate/run", {
                scope, create_pending_for_unmatched: addUnmatched, only_missing: true,
            });
            setResult(data);
            toast.success(`Migrated ${data.jobs_updated} jobs + ${data.seeds_updated} seeds`);
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setRunning(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="sc-migrate-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-ikb" /> Migrate skills to catalog</DialogTitle>
                    <DialogDescription>
                        Scan your jobs and JD seeds; map each free-text skill to a global catalog entry so
                        matching, trend analysis, and reporting work uniformly.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Scope:</label>
                        {[["all","All"],["jobs","Jobs only"],["seeds","Seeds only"]].map(([v,l]) => (
                            <button key={v} type="button" onClick={() => setScope(v)}
                                data-testid={`sc-migrate-scope-${v}`}
                                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest border ${scope === v ? "border-ikb bg-ikb text-white" : "border-border bg-white hover:border-ikb"}`}
                            >{l}</button>
                        ))}
                        <Button onClick={runPreview} disabled={previewing} className="rounded-none bg-ikb hover:bg-ikb/90 h-8 ml-auto" data-testid="sc-migrate-preview-btn">
                            {previewing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                            Preview
                        </Button>
                    </div>

                    {preview && (
                        <div className="border border-border bg-white p-3 space-y-3" data-testid="sc-migrate-preview">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                <KPICell label="Jobs scanned" value={preview.jobs_scanned} />
                                <KPICell label="Need migration" value={preview.jobs_needing_migration + preview.seeds_needing_migration} tint="ikb" />
                                <KPICell label="Matched skills" value={preview.matched_count} tint="emerald" />
                                <KPICell label="Unmatched" value={preview.unmatched_count} tint={preview.unmatched_count > 0 ? "amber" : "muted"} />
                            </div>
                            {preview.unmatched?.length > 0 && (
                                <details open>
                                    <summary className="cursor-pointer text-amber-800 text-xs font-mono uppercase tracking-widest">
                                        Unmatched skill tokens ({preview.unmatched.length})
                                    </summary>
                                    <div className="mt-2 max-h-40 overflow-y-auto border border-amber-200 p-2 flex flex-wrap gap-1">
                                        {preview.unmatched.map((u) => (
                                            <span key={u.name} className="text-[10px] border border-amber-400 bg-amber-50 px-1.5 py-0.5"
                                                  title={`In ${u.in_jobs.length} jobs, ${u.in_seeds.length} seeds`}>
                                                {u.name}
                                            </span>
                                        ))}
                                    </div>
                                    <label className="flex items-center gap-1.5 mt-2 text-xs">
                                        <input type="checkbox" checked={addUnmatched} onChange={(e) => setAddUnmatched(e.target.checked)}
                                               data-testid="sc-migrate-add-unmatched" />
                                        <span>Also add unmatched tokens to catalog as <b>pending</b> (recommended)</span>
                                    </label>
                                </details>
                            )}
                            {preview.matched?.length > 0 && (
                                <details>
                                    <summary className="cursor-pointer text-emerald-800 text-xs font-mono uppercase tracking-widest">
                                        Matched ({preview.matched.length})
                                    </summary>
                                    <div className="mt-2 max-h-40 overflow-y-auto text-[11px]">
                                        {preview.matched.slice(0, 50).map((m) => (
                                            <div key={m.name} className="flex items-center gap-2 py-0.5">
                                                <span className="font-mono">{m.name}</span>
                                                <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                                <span className="text-emerald-700">{m.match_name}</span>
                                                {m.trend_status && <span className="text-[10px] font-mono uppercase text-muted-foreground">· {m.trend_status}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                            <Button onClick={runMigrate} disabled={running} className="rounded-none bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="sc-migrate-run-btn">
                                {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                                Run migration
                            </Button>
                        </div>
                    )}
                    {result && (
                        <div className="border border-emerald-300 bg-emerald-50 p-3 text-xs" data-testid="sc-migrate-result">
                            Migrated <b>{result.jobs_updated}</b> jobs and <b>{result.seeds_updated}</b> seeds ·
                            <b> {result.skipped_up_to_date}</b> already up-to-date
                            {result.created_pending_for_unmatched && " · unmatched tokens added as pending"}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function KPICell({ label, value, tint = "muted" }) {
    const map = { ikb: "text-ikb", emerald: "text-emerald-700", amber: "text-amber-700", muted: "text-foreground" };
    return (
        <div className="border border-border p-2 text-center">
            <div className={`font-display font-black text-lg ${map[tint] || map.muted}`}>{value ?? "—"}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase S4 — Reusable Skill Evaluation Grid (used by CandidateProfileDialog)
// ═══════════════════════════════════════════════════════════════════════════
export function SkillEvaluationGrid({ applicationId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        if (!applicationId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/skill-center/applications/${applicationId}/evaluations`);
            setData(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [applicationId]);

    useEffect(() => { load(); }, [load]);

    const generate = async (force = false) => {
        setGenerating(true);
        try {
            const { data } = await api.post(`/skill-center/applications/${applicationId}/evaluations/generate`, { force });
            if (data.skipped) toast.info(`Skipped: ${data.reason}`);
            else toast.success(`AI wrote ${data.written} evaluations`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setGenerating(false); }
    };

    if (loading && !data) return <div className="border border-border bg-white p-6 text-center text-sm text-muted-foreground">Loading…</div>;
    if (!data) return null;

    const counts = data.counts || {};
    const rows = data.evaluations || [];

    return (
        <div className="space-y-3" data-testid="sc-eval-grid">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                    Skill grid for this application
                </div>
                <Button size="sm" variant="outline" className="rounded-none h-7 ml-auto" onClick={() => generate(false)} disabled={generating} data-testid="sc-eval-generate">
                    {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    AI generate
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => generate(true)} disabled={generating} data-testid="sc-eval-regenerate">
                    Re-run
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={load} data-testid="sc-eval-refresh">
                    <RefreshCw className="w-3 h-3" />
                </Button>
            </div>
            <div className="grid grid-cols-4 border border-border bg-white divide-x divide-border">
                {[
                    { k: "possesses", label: "Possesses", color: "text-emerald-700" },
                    { k: "partial",   label: "Partial",   color: "text-amber-700" },
                    { k: "gap",       label: "Gap",       color: "text-rose-700" },
                    { k: "unknown",   label: "Unknown",   color: "text-slate-500" },
                ].map((b) => (
                    <div key={b.k} className="p-2 text-center">
                        <div className={`font-display font-black text-xl ${b.color}`} data-testid={`sc-eval-count-${b.k}`}>{counts[b.k] || 0}</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{b.label}</div>
                    </div>
                ))}
            </div>
            {rows.length === 0 && (
                <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                    No evaluations yet. Click <b>AI generate</b> to score this candidate against the job&apos;s required skills.
                </div>
            )}
            {rows.length > 0 && (
                <div className="border border-border bg-white divide-y divide-border" data-testid="sc-eval-list">
                    {rows.map((r) => <EvalRow key={r.id} r={r} onDelete={async () => {
                        if (!confirm(`Delete evaluation for ${r.skill_name}?`)) return;
                        try {
                            await api.delete(`/skill-center/applications/${applicationId}/evaluations/${r.id}`);
                            toast.success("Deleted");
                            load();
                        } catch (err) { toast.error(formatApiError(err)); }
                    }} />)}
                </div>
            )}
        </div>
    );
}

function EvalRow({ r, onDelete }) {
    const map = {
        possesses: { cls: "border-emerald-500 bg-emerald-50 text-emerald-800", icon: "✓" },
        partial:   { cls: "border-amber-500 bg-amber-50 text-amber-800", icon: "◐" },
        gap:       { cls: "border-rose-500 bg-rose-50 text-rose-800", icon: "✗" },
        unknown:   { cls: "border-slate-300 bg-slate-50 text-slate-600", icon: "?" },
    };
    const st = map[r.status] || map.unknown;
    return (
        <div className="p-2 flex items-start gap-3 group" data-testid={`sc-eval-row-${(r.skill_name || "").replace(/\s+/g,"-").toLowerCase()}`}>
            <span className={`inline-flex items-center justify-center w-6 h-6 border ${st.cls} font-mono text-sm shrink-0`}>{st.icon}</span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm">{r.skill_name}</span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest border px-1 ${st.cls}`}>{r.status}</span>
                    {r.category && <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{(r.category || "").replace("_"," ")}</span>}
                    {r.trend_status && <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">· {r.trend_status}</span>}
                    {typeof r.confidence === "number" && <span className="text-[10px] font-mono text-muted-foreground">conf {Math.round((r.confidence || 0) * 100)}%</span>}
                </div>
                {r.evidence && <div className="text-xs text-foreground/70 mt-0.5">{r.evidence}</div>}
                {r.trend_replaced_by && (
                    <div className="text-[10px] text-rose-700 mt-0.5">
                        <ArrowUpRight className="w-3 h-3 inline" /> Consider <b>{r.trend_replaced_by}</b> instead.
                    </div>
                )}
            </div>
            {r.source && <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground shrink-0" title={r.source}>{r.source.replace(/_/g," ")}</span>}
            {onDelete && (
                <button
                    type="button"
                    onClick={onDelete}
                    className="opacity-0 group-hover:opacity-100 p-1 border border-border hover:border-rose-500 hover:text-rose-700 transition-opacity"
                    title="Delete evaluation"
                    data-testid={`sc-eval-delete-${(r.skill_name || "").replace(/\s+/g,"-").toLowerCase()}`}
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            )}
        </div>
    );
}

